import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY  = 'admin_token';
const TTL_MS       = parseInt(process.env.REACT_APP_ADMIN_TOKEN_TTL_MS ?? '28800000', 10);

// The token's signature is checked only by the server (GET /api/admin/whoami and
// every admin call). The signing key must never be in the browser bundle.

/**
 * useAdminAuth — manages the admin token lifecycle.
 *
 * Flow:
 *   1. On mount, check for ?token= in the URL (redirect from ICON platform).
 *   2. Reject it early if its timestamp is past the TTL (no signature check here).
 *   3. Store in sessionStorage and strip from the URL.
 *   4. Confirm with the server via GET /api/admin/whoami.
 *   5. Expose { status, token, signOut }.
 *
 * status: 'checking' | 'allowed' | 'expired' | 'blocked'
 */
export function useAdminAuth() {
  const [status, setStatus] = useState('checking');
  const [token, setToken]   = useState(null);

  const signOut = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken(null);
    setStatus('blocked');
  }, []);

  useEffect(() => {
    async function init() {
      // Try URL param first (fresh redirect from ICON)
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('token');

      const candidate = urlToken || sessionStorage.getItem(STORAGE_KEY);
      if (!candidate) { setStatus('blocked'); return; }

      // Quick expiry check from the token's timestamp; the server checks the signature.
      try {
        const tsPart  = candidate.split('.')[0];
        const timestamp = parseInt(atob(tsPart.replace(/-/g, '+').replace(/_/g, '/')), 10);
        if (isNaN(timestamp) || Date.now() - timestamp > TTL_MS) {
          sessionStorage.removeItem(STORAGE_KEY);
          setStatus('expired');
          return;
        }
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
        setStatus('expired');
        return;
      }

      // Strip from address bar if it came from the URL
      if (urlToken) {
        const clean = new URL(window.location.href);
        clean.searchParams.delete('token');
        window.history.replaceState({}, '', clean.toString());
        sessionStorage.setItem(STORAGE_KEY, candidate);
      }

      // Server round-trip to confirm the token's signature.
      try {
        const resp = await fetch('/api/admin/whoami', {
          headers: { Authorization: `Bearer ${candidate}` },
        });
        if (resp.status === 401) {
          sessionStorage.removeItem(STORAGE_KEY);
          setStatus('expired');
          return;
        }
        // Other errors fall through; every admin call is checked by the server again
      } catch {
        // Network error / server unreachable — fall through and allow
      }

      setToken(candidate);
      setStatus('allowed');
    }

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { status, token, signOut };
}
